/**
 * Sentry Configuration
 *
 * F10: Error tracking and monitoring for agustinmusi.com
 *
 * Features:
 * - Automatic error capture for React
 * - Performance monitoring
 * - User context tracking
 * - Breadcrumb tracking
 * - Environment-aware configuration
 */

import * as Sentry from '@sentry/react'

export function initSentry() {
  // Only initialize in production or if explicitly enabled
  const isDevelopment = import.meta.env.MODE === 'development'
  const dsn = import.meta.env.VITE_SENTRY_DSN

  if (!dsn) {
    console.warn('[Sentry] DSN not configured. Error tracking disabled.')
    return
  }

  Sentry.init({
    dsn,

    // Environment configuration
    environment: import.meta.env.MODE || 'production',
    release: import.meta.env.VITE_APP_VERSION || 'unknown',

    // Performance Monitoring
    tracesSampleRate: isDevelopment ? 1.0 : 0.1, // 100% in dev, 10% in prod

    // Session Replay (helpful for debugging)
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Integrations
    integrations: [
      // Browser Tracing for performance monitoring
      Sentry.browserTracingIntegration({
        // Trace navigation events
        enableInp: true,
      }),

      // Session Replay for visual debugging
      Sentry.replayIntegration({
        maskAllText: true, // Privacy: mask all text
        blockAllMedia: true, // Privacy: block all media
      }),
    ],

    // Filtering
    beforeSend(event, hint) {
      // Don't send errors in development (unless explicitly enabled)
      if (isDevelopment && !import.meta.env.VITE_SENTRY_DEBUG) {
        console.log('[Sentry] Error captured (dev mode, not sent):', event)
        return null
      }

      // Filter out known non-critical errors
      const error = hint.originalException
      if (error instanceof Error) {
        // Ignore ResizeObserver errors (common, harmless)
        if (error.message.includes('ResizeObserver')) {
          return null
        }

        // Ignore network errors from browser extensions
        if (error.message.includes('chrome-extension://')) {
          return null
        }
      }

      return event
    },

    // Ignore certain URLs
    ignoreErrors: [
      // Browser extensions
      'chrome-extension://',
      'moz-extension://',

      // Third-party scripts
      'google-analytics',
      'googletagmanager',

      // Common non-errors
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
    ],
  })

  // Set default tags
  Sentry.setTag('app', 'agustinmusi.com')

  console.log('[Sentry] Initialized successfully')
}

/**
 * Manually capture an exception
 */
export function captureException(error: Error, context?: Record<string, any>) {
  if (context) {
    Sentry.setContext('custom', context)
  }
  Sentry.captureException(error)
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(message: string, data?: Record<string, any>) {
  Sentry.addBreadcrumb({
    message,
    level: 'info',
    data,
    timestamp: Date.now() / 1000,
  })
}

/**
 * Set user context
 */
export function setUser(user: { id: string; email?: string; username?: string }) {
  Sentry.setUser(user)
}

/**
 * Clear user context (on logout)
 */
export function clearUser() {
  Sentry.setUser(null)
}
